import { useState } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  Container,
  Grid,
  MenuItem,
  Alert,
  CircularProgress,
} from "@mui/material";
import { location } from "../../data/Fleet/Location";
import { apiVariable } from "../../api/apiVariable";
import { getPublicRequestError } from "../../helpers/publicRequestError";

const ExternalLeadFormPage = () => {
  const [formData, setFormData] = useState({
    company: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    contactName: "",
    directPhone: "",
    email: "",
    dot: "",
    mc: "",
  });

  const [errors, setErrors] = useState<any>({});
  const [submissionStatus, setSubmissionStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.company || formData.company.length > 100) {
      newErrors.company =
        "Company is required and cannot exceed 100 characters.";
    }

    if (!formData.address || formData.address.length > 200) {
      newErrors.address =
        "Address is required and cannot exceed 200 characters.";
    }

    if (!formData.city || formData.city.length > 100) {
      newErrors.city = "City is required and cannot exceed 100 characters.";
    }

    if (!formData.state) {
      newErrors.state = "State is required.";
    }

    if (!formData.zip || formData.zip.length > 20) {
      newErrors.zip = "Zip code is required and cannot exceed 20 characters.";
    }

    if (!formData.contactName || formData.contactName.length > 100) {
      newErrors.contactName =
        "Contact name is required and cannot exceed 100 characters.";
    }

    const phoneRegex = /^\d{10}$/;
    if (!formData.directPhone || !phoneRegex.test(formData.directPhone)) {
      newErrors.directPhone = "Valid direct phone is required (10 digits).";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (
      !formData.email ||
      !emailRegex.test(formData.email) ||
      formData.email.length > 254
    ) {
      newErrors.email =
        "Valid email is required and cannot exceed 254 characters.";
    }

    if (formData.dot && formData.dot.length > 50) {
      newErrors.dot = "DOT number cannot exceed 50 characters.";
    }

    if (formData.mc && formData.mc.length > 50) {
      newErrors.mc = "MC number cannot exceed 50 characters.";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: any) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });

    // Clear the error when the user starts typing
    setErrors({
      ...errors,
      [e.target.name]: "",
    });
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);
    setSubmissionStatus("");

    try {
      const response = await fetch(`${apiVariable}/external-lead`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmissionStatus("You have applied successfully!");
        setFormData({
          company: "",
          address: "",
          city: "",
          state: "",
          zip: "",
          contactName: "",
          directPhone: "",
          email: "",
          dot: "",
          mc: "",
        });
      } else if (response.status === 400) {
        const data = await response.json();
        setSubmissionStatus(
          data?.errors
            ? Object.values(data.errors).join(" ")
            : "Invalid input data.",
        );
      } else {
        setSubmissionStatus(
          getPublicRequestError(
            response.status,
            response.headers.get("Retry-After"),
          ),
        );
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmissionStatus("An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 4,
          padding: 3,
          boxShadow: 3,
          borderRadius: 2,
          backgroundColor: "#fff",
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Submit Your Company
        </Typography>

        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Fields with <span style={{ color: "red" }}>*</span> are mandatory
        </Typography>

        {submissionStatus && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {submissionStatus}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="company"
                name="company"
                label="Company *"
                value={formData.company}
                onChange={handleChange}
                error={Boolean(errors.company)}
                helperText={errors.company}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                id="address"
                name="address"
                label="Address *"
                value={formData.address}
                onChange={handleChange}
                error={Boolean(errors.address)}
                helperText={errors.address}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                id="city"
                name="city"
                label="City *"
                value={formData.city}
                onChange={handleChange}
                error={Boolean(errors.city)}
                helperText={errors.city}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                id="state"
                name="state"
                label="State *"
                value={formData.state}
                onChange={handleChange}
                error={Boolean(errors.state)}
                helperText={errors.state}
                required
              >
                {location.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                id="zip"
                name="zip"
                label="Zip Code *"
                value={formData.zip}
                onChange={handleChange}
                error={Boolean(errors.zip)}
                helperText={errors.zip}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                id="contactName"
                name="contactName"
                label="Contact Name *"
                value={formData.contactName}
                onChange={handleChange}
                error={Boolean(errors.contactName)}
                helperText={errors.contactName}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                id="directPhone"
                name="directPhone"
                label="Direct Phone *"
                value={formData.directPhone}
                onChange={handleChange}
                error={Boolean(errors.directPhone)}
                helperText={errors.directPhone}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                id="email"
                name="email"
                label="Email *"
                type="email"
                value={formData.email}
                onChange={handleChange}
                error={Boolean(errors.email)}
                helperText={errors.email}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                id="dot"
                name="dot"
                label="DOT Number"
                value={formData.dot}
                onChange={handleChange}
                error={Boolean(errors.dot)}
                helperText={errors.dot}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                id="mc"
                name="mc"
                label="MC Number"
                value={formData.mc}
                onChange={handleChange}
                error={Boolean(errors.mc)}
                helperText={errors.mc}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                color="primary"
                variant="contained"
                fullWidth
                type="submit"
                disabled={loading}
                startIcon={loading && <CircularProgress size={20} />}
              >
                {loading ? "Submitting..." : "Submit"}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Box>
    </Container>
  );
};

export default ExternalLeadFormPage;
